/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * A compact chip/token multi-select (per the UI design): selected values render
 * as removable chips inside an input-height control; clicking it opens a
 * type-to-filter menu of the remaining options. Chosen here over a native
 * <select multiple> (undiscoverable) or an always-open checkbox list (too tall),
 * so the [states | distributor] row stays aligned and scannable.
 */
import { useRef, useState } from "react";
import { useClickOutside } from "../../../shared/hooks/useClickOutside.js";

export default function StatesMultiSelect({ options = [], value = [], onChange, placeholder = "Select…", disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useClickOutside(ref, () => { setOpen(false); setQuery(""); }, open);

  const selected = Array.isArray(value) ? value : [];
  const remaining = options.filter((o) => !selected.includes(o));
  const filtered = query
    ? remaining.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : remaining;

  function add(opt) {
    onChange([...selected, opt]);
    setQuery("");
  }
  function remove(opt) {
    onChange(selected.filter((s) => s !== opt));
  }

  return (
    <div className="it-ms" ref={ref}>
      <div
        className={`it-ms-control ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen(true)}
      >
        {selected.map((s) => (
          <span className="it-ms-chip" key={s}>
            {s}
            {!disabled && (
              <button type="button" className="it-ms-x" onClick={(e) => { e.stopPropagation(); remove(s); }} aria-label={`Remove ${s}`}>✕</button>
            )}
          </span>
        ))}
        <input
          className="it-ms-input"
          value={query}
          disabled={disabled}
          placeholder={selected.length === 0 ? placeholder : ""}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        />
      </div>
      {open && !disabled && (
        <div className="it-ms-menu" role="listbox">
          {filtered.length === 0 ? (
            <div className="it-ms-empty">{remaining.length === 0 ? "All selected" : "No matches"}</div>
          ) : (
            filtered.map((o) => (
              <button type="button" key={o} className="it-ms-option" onClick={() => add(o)} role="option">
                {o}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
